import { FileGroupItemDto } from 'api/risk';
import clsx from 'clsx';
import { CardTabIcon, CardTabItem, CardTabs, CardTabText } from 'components/ui/CardTabs';
import { NsKey } from 'enums/NsKey';
import { formatPrice } from './ContractualizationTabs';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { AlertCircle, BadgeCheck, Building2, Check, Send, User } from 'lucide-react';
import { FILE_GROUP_SWITCH_STATE } from 'features/subscription-process/utils/fileGroupSwitch';
import { useNavigate, useParams } from 'react-router-dom';

interface FileGroupProps {
  /** Files of the group, the current one included; nothing is shown with fewer than two. */
  items: FileGroupItemDto[];
}

const STATUS_DISPLAY: Record<string, { className: string; icon: React.ReactNode }> = {
  TO_COMPLETE: { className: 'bg-amber-100 text-amber-700', icon: <AlertCircle className="w-3 h-3" /> },
  MANDATE_SIGNED: { className: 'bg-primary/10 text-primary', icon: <Check className="w-3 h-3" /> },
  CONTRACT_SENT: { className: 'bg-blue-100 text-blue-700', icon: <Send className="w-3 h-3" /> },
  SIGNED: { className: 'bg-green-100 text-green-700', icon: <BadgeCheck className="w-3 h-3" /> }
};

/** Label above the card: whose subscriptions these files are. */
export const FileGroupTitle: React.FC<FileGroupProps> = ({ items }) => {
  const { t } = useDynamicTranslation([NsKey.Contractualization]);

  if (items.length < 2) return null;

  const companyName = (items.find(item => item.isCurrent) ?? items[0]).companyName;

  return (
    <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
      {t('CONTRACTUALIZATION.FILE_GROUP.TITLE', { name: companyName, count: items.length })}
    </p>
  );
};

/**
 * File tabs at the top of the contractualisation card when files were created together (legal
 * protection "for the company" and "for the manager"), drawn by the shared CardTabs. A tab says who
 * the file is for, not what it holds: a file may carry several quotes of different products, told
 * apart on the quotes themselves, one level below. A file with a single quote has no quotes below:
 * its tab then carries the product and the price as well.
 */
export const FileGroupSelector: React.FC<FileGroupProps> = ({ items }) => {
  const { t } = useDynamicTranslation([NsKey.Contractualization]);
  const navigate = useNavigate();
  // The displayed file follows the URL: the clicked tab comes forward at once, before the data
  // of its file (and the `isCurrent` flags, read for the previous file) are loaded.
  const { riskId } = useParams<{ riskId: string }>();

  // A file without group, or alone in its group: nothing to choose.
  if (items.length < 2) return null;

  const tabs: CardTabItem[] = items.map(item => {
    const status = STATUS_DISPLAY[item.status];

    return {
      id: item.riskId,
      isActive: riskId ? item.riskId === riskId : item.isCurrent,
      disabled: !item.quoteId,
      onSelect: () =>
        navigate(`/simulation/contractualisation/${item.riskId}/${item.quoteId}`, { state: FILE_GROUP_SWITCH_STATE }),
      render: isActive => {
        const renderStatus = (className?: string) => (
          <span
            className={clsx(
              className,
              'flex-none inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-xs font-bold whitespace-nowrap',
              status?.className ?? 'bg-gray-100 text-gray-600'
            )}
          >
            {status?.icon}
            {t(`CONTRACTUALIZATION.FILE_GROUP.STATUS.${item.status}`)}
          </span>
        );
        const icon = (
          <CardTabIcon isActive={isActive}>
            {item.beneficiaryName ? <User className="w-[19px] h-[19px]" /> : <Building2 className="w-[19px] h-[19px]" />}
          </CardTabIcon>
        );

        // A single quote: the tab tells everything, no quote is listed below.
        if (item.quotesCount === 1 && item.productLabel) {
          return (
            <>
              {icon}

              <CardTabText
                isActive={isActive}
                eyebrow={item.beneficiaryName
                  ? t('CONTRACTUALIZATION.FILE_GROUP.FOR_MANAGER_NAMED', { name: item.beneficiaryName })
                  : t('CONTRACTUALIZATION.FILE_GROUP.FOR_COMPANY_NAMED', { name: item.companyName })}
              >
                {item.productLabel}
              </CardTabText>

              <span className="ml-auto flex-none flex flex-col items-end gap-1">
                {item.clientPrice !== undefined && item.clientPrice !== null && (
                  <span
                    className={clsx(
                      'text-sm font-bold tabular-nums whitespace-nowrap',
                      isActive ? 'text-gray-900' : 'text-gray-700'
                    )}
                  >
                    {formatPrice(item.clientPrice)}
                  </span>
                )}
                {renderStatus()}
              </span>
            </>
          );
        }

        return (
          <>
            {icon}

            <CardTabText
              isActive={isActive}
              eyebrow={item.beneficiaryName
                ? t('CONTRACTUALIZATION.FILE_GROUP.FOR_MANAGER')
                : t('CONTRACTUALIZATION.FILE_GROUP.FOR_COMPANY')}
            >
              {item.beneficiaryName ?? item.companyName}
            </CardTabText>

            {renderStatus('ml-auto')}
          </>
        );
      }
    };
  });

  return <CardTabs items={tabs} />;
};
